import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { hrmApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'balances' | 'mine' | 'approvals' | 'accrual';

/**
 * Leave — FR-HR-03
 * Balances, applications, approvals, year-end accrual runner.
 */
export function LeavePage() {
  const [tab, setTab] = useState<Tab>('balances');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'balances',  label: 'My balances' },
    { key: 'mine',      label: 'My applications' },
    { key: 'approvals', label: 'Approvals' },
    { key: 'accrual',   label: 'Accrual' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Leave"
        subtitle="Apply for leave, view balances, approve pending applications, and run year-end accruals."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'balances' && <BalancesTab />}
      {tab === 'mine' && <MyApplicationsTab />}
      {tab === 'approvals' && <ApprovalsTab />}
      {tab === 'accrual' && <AccrualTab />}
    </div>
  );
}

// ============================================================
// BalancesTab
// ============================================================
function BalancesTab() {
  const [showApply, setShowApply] = useState(false);

  const todayQ = useQuery({
    queryKey: ['attendance-today'],
    queryFn: () => hrmApi.getTodayAttendance(),
  });
  const employeeId = todayQ.data?.employeeId;

  const balQ = useQuery({
    queryKey: ['leave-balances', employeeId],
    queryFn: () => hrmApi.getLeaveBalances(employeeId!),
    enabled: !!employeeId,
  });

  const kpis = useMemo(() => {
    const balances = balQ.data?.balances ?? [];
    let totalAvailable = 0, totalUsed = 0;
    for (const b of balances) {
      totalAvailable += b.balance;
      totalUsed += b.used;
    }
    return { totalAvailable, totalUsed, types: balances.length };
  }, [balQ.data]);

  return (
    <div>
      {!employeeId ? (
        <div className="card">
          <EmptyState
            message="No employee record linked"
            hint="Your account isn't linked to an employee profile. Contact HR to be set up."
          />
        </div>
      ) : balQ.isLoading ? (
        <div className="card"><LoadingState rows={3} /></div>
      ) : balQ.error ? (
        <ErrorState error={balQ.error} />
      ) : (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <KpiTile label="Available days" value={kpis.totalAvailable.toFixed(1)} accent="sage" />
            <KpiTile label="Used this year" value={kpis.totalUsed.toFixed(1)} />
            <KpiTile label="Leave types" value={kpis.types} />
          </div>

          <div className="flex justify-end mb-4">
            <button className="btn-primary" onClick={() => setShowApply(true)}>+ Apply for leave</button>
          </div>

          {balQ.data.balances.length === 0 ? (
            <div className="card">
              <EmptyState
                message="No leave types configured"
                hint="Ask HR to configure leave types in Admin settings."
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(balQ.data?.balances ?? []).map((b: any) => {
                const used = b.used;
                const total = b.accrued + b.carryForward;
                const pct = total > 0 ? (used / total) * 100 : 0;
                const remainingPct = 100 - pct;
                const barTone =
                  remainingPct >= 50 ? 'bg-sage-500' :
                  remainingPct >= 25 ? 'bg-amber-500' :
                                       'bg-danger';
                return (
                  <div key={b.leaveTypeId} className="card p-5">
                    <div className="flex justify-between items-start mb-4">
                      <div className="min-w-0">
                        <div className="eyebrow text-ink-500">{b.isPaid ? 'Paid' : 'Unpaid'}</div>
                        <div className="font-display font-bold text-ink-900 mt-0.5">{b.name}</div>
                        <div className="font-mono text-2xs text-ink-400 mt-0.5">{b.code}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display font-bold text-3xl text-ink-900 tabular-nums tracking-tight">
                          {b.balance}
                        </div>
                        <div className="text-2xs text-ink-500 uppercase tracking-wider font-semibold">
                          days left
                        </div>
                      </div>
                    </div>
                    <div className="h-2 bg-canvas-subtle rounded-full overflow-hidden mb-2">
                      <div
                        className={clsx('h-full transition-all', barTone)}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-2xs text-ink-500">
                      <span className="font-mono tabular-nums">Used {used}</span>
                      <span className="font-mono tabular-nums">Total {total}</span>
                    </div>
                    <div className="text-2xs text-ink-400 mt-2 pt-2 border-t border-ink-100">
                      Accrued {b.accrued}
                      {b.carryForward > 0 && <> · +{b.carryForward} carried forward</>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showApply && <ApplyLeaveModal onClose={() => setShowApply(false)} onApplied={() => setShowApply(false)} />}
    </div>
  );
}

// ============================================================
// MyApplicationsTab
// ============================================================
function MyApplicationsTab() {
  const q = useQuery({
    queryKey: ['leave-applications-mine'],
    queryFn: () => hrmApi.listLeaveApplications({ limit: 200 }),
  });
  const qc = useQueryClient();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const cancelMut = useMutation({
    mutationFn: (id: string) => hrmApi.cancelLeave(id),
    onSuccess: () => {
      toast.success('Cancelled');
      qc.invalidateQueries({ queryKey: ['leave-applications-mine'] });
      qc.invalidateQueries({ queryKey: ['leave-balances'] });
      setCancellingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No leave applications yet"
              hint="Apply for leave from the My balances tab."
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Period</th>
                    <th className="text-right">Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Applied</th>
                    <th className="text-right w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((a: any) => {
                    const canCancel = ['pending', 'approved'].includes(a.status);
                    return (
                      <tr key={a.id}>
                        <td className="font-mono text-xs font-semibold text-ink-900">{a.leave_type_code}</td>
                        <td className="text-xs text-ink-700 font-mono tabular-nums">
                          {new Date(a.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          {a.start_date !== a.end_date && (
                            <>
                              <span className="mx-1 text-ink-400">→</span>
                              {new Date(a.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </>
                          )}
                          {a.is_half_day && (
                            <span className="ml-1 text-ink-500">(½ {a.half_day_period?.replace('_', ' ')})</span>
                          )}
                        </td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                          {parseFloat(a.days).toFixed(1)}
                        </td>
                        <td className="text-sm text-ink-700 max-w-xs truncate">{a.reason}</td>
                        <td><LeaveStatusBadge status={a.status} /></td>
                        <td className="text-2xs text-ink-500 font-mono tabular-nums">
                          {new Date(a.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="text-right">
                          {canCancel && (
                            <button
                              className="text-2xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                              onClick={() => setCancellingId(a.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {cancellingId && (
        <CancelLeaveDialog
          loading={cancelMut.isPending}
          onClose={() => setCancellingId(null)}
          onConfirm={() => cancelMut.mutate(cancellingId)}
        />
      )}
    </div>
  );
}

function CancelLeaveDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-4">
        <div className="eyebrow text-danger mb-1">Cancel · Leave</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Cancel leave application?</h2>
        <p className="text-sm text-ink-500 mt-2">
          Days will be returned to your balance. This cannot be undone — you'd need to apply again.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep it</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Cancelling…' : 'Cancel leave'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// ApprovalsTab
// ============================================================
function ApprovalsTab() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['leave-applications-all', statusFilter],
    queryFn: () => hrmApi.listLeaveApplications({
      status: statusFilter || undefined, limit: 200,
    }),
  });
  const qc = useQueryClient();

  const approveMut = useMutation({
    mutationFn: (id: string) => hrmApi.approveLeave(id),
    onSuccess: (d) => {
      toast.success(`Approved · ${d.daysDeducted} days deducted`);
      qc.invalidateQueries({ queryKey: ['leave-applications-all'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => hrmApi.rejectLeave(id, reason),
    onSuccess: () => {
      toast.success('Rejected');
      qc.invalidateQueries({ queryKey: ['leave-applications-all'] });
      setRejectingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'pending',   label: 'Pending' },
    { value: 'approved',  label: 'Approved' },
    { value: 'rejected',  label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div>
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message={statusFilter ? `No ${statusFilter} applications` : 'No applications to review'}
              hint={statusFilter ? 'Try a different filter.' : 'Pending applications will appear here for approval.'}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Period</th>
                    <th className="text-right">Days</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th className="text-right w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((a: any) => (
                    <tr key={a.id}>
                      <td>
                        <div className="font-medium text-ink-900">{a.employee_name}</div>
                        <div className="text-2xs text-ink-500 font-mono mt-0.5">{a.employee_code}</div>
                      </td>
                      <td className="font-mono text-xs font-semibold text-ink-900">{a.leave_type_code}</td>
                      <td className="text-xs text-ink-700 font-mono tabular-nums">
                        {new Date(a.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        {a.start_date !== a.end_date && (
                          <>
                            <span className="mx-1 text-ink-400">→</span>
                            {new Date(a.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </>
                        )}
                      </td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        {parseFloat(a.days).toFixed(1)}
                      </td>
                      <td className="text-sm text-ink-700 max-w-xs truncate">{a.reason}</td>
                      <td><LeaveStatusBadge status={a.status} /></td>
                      <td className="text-right">
                        {a.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <button
                              className="text-2xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                              disabled={approveMut.isPending}
                              onClick={() => approveMut.mutate(a.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="text-2xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                              onClick={() => setRejectingId(a.id)}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {rejectingId && (
        <RejectLeaveDialog
          loading={rejectMut.isPending}
          onClose={() => setRejectingId(null)}
          onConfirm={(reason) => rejectMut.mutate({ id: rejectingId, reason })}
        />
      )}
    </div>
  );
}

function RejectLeaveDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Reject · Leave</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject leave application</h2>
        <p className="text-sm text-ink-500 mt-2">
          The employee will be notified with your reason. Consider approving instead if it's a minor scheduling issue.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Conflicts with quarter-end close — please reschedule for the following week"
        />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// AccrualTab
// ============================================================
function AccrualTab() {
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const qc = useQueryClient();

  const runMut = useMutation({
    mutationFn: () => hrmApi.runLeaveAccrual({ periodYear }),
    onSuccess: (d) => {
      toast.success(
        `${d.employeesProcessed} employees · ${d.rowsCreated} created, ${d.rowsUpdated} updated · ${d.totalAccrued} days accrued`,
        { duration: 6000 }
      );
      qc.invalidateQueries({ queryKey: ['leave-balances'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-8 max-w-xl">
      <div className="eyebrow text-ink-500 mb-1">Payroll admin</div>
      <h3 className="font-display font-bold text-xl text-ink-900">Run leave accrual</h3>
      <p className="text-sm text-ink-600 mt-3 leading-relaxed">
        Walks every active employee × leave type. Annual-method types get the full entitlement set idempotently. Monthly-method types grant one month's worth, but only if no accrual has run this calendar month.
      </p>
      <p className="text-sm text-ink-600 mt-3 leading-relaxed">
        Year rollover creates new balance rows with carry-forward from the prior year, capped at each type's <code className="text-xs bg-canvas-subtle px-1 py-0.5 rounded">carry_forward_max_days</code>.
      </p>

      <div className="mt-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">Period year</label>
          <input
            type="number"
            min={2020}
            max={2030}
            className="input w-32 tabular-nums"
            value={periodYear}
            onChange={(e) => setPeriodYear(parseInt(e.target.value) || new Date().getFullYear())}
          />
        </div>
        <button
          className="btn-primary"
          disabled={runMut.isPending}
          onClick={() => runMut.mutate()}
        >
          {runMut.isPending ? 'Running…' : 'Run accrual'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// ApplyLeaveModal
// ============================================================
function ApplyLeaveModal({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const qc = useQueryClient();
  const typesQ = useQuery({ queryKey: ['leave-types'], queryFn: () => hrmApi.listLeaveTypes() });

  const today = new Date().toISOString().slice(0, 10);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<'first_half' | 'second_half'>('first_half');

  const mut = useMutation({
    mutationFn: () => hrmApi.applyLeave({
      leaveTypeId, startDate, endDate, reason,
      isHalfDay,
      halfDayPeriod: isHalfDay ? halfDayPeriod : undefined,
    }),
    onSuccess: (d) => {
      toast.success(d.status === 'approved' ? `Auto-approved · ${d.days} days` : `Submitted · ${d.days} days pending`);
      qc.invalidateQueries({ queryKey: ['leave-balances'] });
      qc.invalidateQueries({ queryKey: ['leave-applications-mine'] });
      onApplied();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-ink-500 mb-1">People · Leave</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Apply for leave</h2>
        <p className="text-sm text-ink-500 mt-1">
          Auto-approved leave types post immediately; others go through your manager's approval queue.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Leave type</label>
          <select
            className="input"
            value={leaveTypeId}
            onChange={(e) => setLeaveTypeId(e.target.value)}
          >
            <option value="">Select…</option>
            {typesQ.data?.map((t: any) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.is_paid ? 'paid' : 'unpaid'})
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            className="accent-sage-500"
            checked={isHalfDay}
            onChange={(e) => {
              setIsHalfDay(e.target.checked);
              if (e.target.checked) setEndDate(startDate);
            }}
          />
          <span>Half day</span>
        </label>

        {isHalfDay && (
          <div>
            <label className="label">Half day period</label>
            <select
              className="input"
              value={halfDayPeriod}
              onChange={(e) => setHalfDayPeriod(e.target.value as any)}
            >
              <option value="first_half">First half (morning)</option>
              <option value="second_half">Second half (afternoon)</option>
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Start date</label>
            <input
              type="date"
              className="input"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (isHalfDay) setEndDate(e.target.value);
              }}
            />
          </div>
          <div>
            <label className="label">End date</label>
            <input
              type="date"
              className="input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isHalfDay}
            />
          </div>
        </div>

        <div>
          <label className="label">Reason <span className="text-danger">*</span></label>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Family vacation"
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!leaveTypeId || !reason || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </Modal>
  );
}

function LeaveStatusBadge({ status }: { status: string }) {
  const cls = {
    pending:   'bg-amber-50 text-amber-800 ring-amber-200',
    approved:  'bg-sage-50 text-sage-700 ring-sage-200',
    rejected:  'bg-red-50 text-red-700 ring-red-200',
    cancelled: 'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
